import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';

/**
 * Generic multi-select dropdown filter.
 * - Defaults to "all selected" when wired with the same options as parent state.
 * - Renders the popover via React portal so it isn't clipped by ancestors with
 *   overflow: hidden (e.g. Framer-Motion collapsible filter rows).
 *
 * Props:
 *   label        – fallback button text when everything is selected (e.g. "All Places")
 *   options      – string[]   list of available choices
 *   selected     – string[]   currently selected subset
 *   onChange     – (next: string[]) => void
 *   testId       – string     base data-testid for the toggle/options
 *   selectionLabel(count, total) – optional custom button label generator
 */
export const MultiSelectFilter = ({
  label,
  options,
  selected,
  onChange,
  testId,
  selectionLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 260 });
  const toggleRef = useRef(null);

  const total = options.length;
  const count = Array.isArray(selected) ? selected.length : 0;
  const narrowed = total > 0 && count < total;

  const buttonText = !narrowed
    ? label
    : selectionLabel
      ? selectionLabel(count, total)
      : `${count} of ${total} selected`;

  const handleToggle = () => {
    if (!open && toggleRef.current) {
      const r = toggleRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 260) });
    }
    setOpen(v => !v);
  };

  const toggleOne = (opt) => {
    const next = selected.includes(opt) ? selected.filter(x => x !== opt) : [...selected, opt];
    onChange(next);
  };

  return (
    <div className="relative" data-testid={`${testId}-multiselect`}>
      <button
        ref={toggleRef}
        type="button"
        onClick={handleToggle}
        className={`h-9 w-full px-3 rounded-md border text-sm text-left flex items-center justify-between gap-1 hover:bg-gray-50 ${narrowed ? 'border-orange-400 bg-orange-50' : 'border-gray-200 bg-white'}`}
        data-testid={`${testId}-toggle`}
      >
        <span className="truncate">{buttonText}</span>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div
            className="fixed z-[61] bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden"
            style={{ top: pos.top, left: pos.left, width: pos.width }}
          >
            <div className="flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 border-b">
              <span className="text-xs text-gray-500">{count} / {total} selected</span>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => onChange([...options])}
                  className="text-xs text-orange-600 hover:underline"
                  data-testid={`${testId}-select-all`}
                >All</button>
                <span className="text-gray-300">|</span>
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="text-xs text-gray-600 hover:underline"
                  data-testid={`${testId}-clear-all`}
                >None</button>
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto py-1">
              {total === 0 && (
                <div className="px-3 py-2 text-sm text-gray-400">No options available</div>
              )}
              {options.map((opt) => {
                const checked = selected.includes(opt);
                return (
                  <label
                    key={opt}
                    className="flex items-center gap-2 px-3 py-1.5 hover:bg-orange-50 cursor-pointer text-sm"
                    data-testid={`${testId}-opt-${opt}`}
                  >
                    <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${checked ? 'bg-orange-500 border-orange-500' : 'border-gray-300 bg-white'}`}>
                      {checked && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                    </span>
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={checked}
                      onChange={() => toggleOne(opt)}
                    />
                    <span className="truncate">{opt}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default MultiSelectFilter;
